/**
 * src/services/spatial/spatialProximity.ts
 *
 * Geometry helpers shared by the country-hover tooltip
 * (`SpatialObjectsCountrySection`) and the cinematic teleport HUD
 * (`AgentTeleportHUD`) for working with GeoLink spatial objects:
 *
 *   - resolving a row's lng/lat (centroid → geometry fallback),
 *   - great-circle distance in metres,
 *   - point-in-(multi)polygon so a row can be assigned to the
 *     country whose polygon actually contains it (rather than the
 *     looser bbox query GeoLink runs server-side, which leaks
 *     neighbours across shared borders),
 *   - human labels / icons for each `SpatialObjectType`.
 *
 * Point-in-polygon is backed by Turf (`@turf/boolean-point-in-polygon`)
 * so interior rings (holes) are honoured. Keep this free of React and
 * Mapbox imports so it stays unit-testable and cheap to call in tight
 * render loops.
 */

import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import type { Polygon, MultiPolygon } from 'geojson';

import type { SpatialObjectType } from './geoLinkTypes';

export interface LngLatLite {
  lng: number;
  lat: number;
}

/** Display label + glyph per spatial-object type. Mirrors the
 *  tooltip's TYPE_META so both surfaces read identically. Unknown
 *  / forward-compat types fall back to the generic object entry. */
export const SPATIAL_TYPE_META: Record<SpatialObjectType, { label: string; icon: string }> = {
  building:                  { label: 'Building',  icon: '🏢' },
  floor:                     { label: 'Floor',     icon: '🪜' },
  zone:                      { label: 'Zone',      icon: '📐' },
  slice:                     { label: 'Slice',     icon: '✂' },
  object_3d:                 { label: '3D object', icon: '🧊' },
  real_estate:               { label: 'Parcel',    icon: '🏞️' },
  event_object:              { label: 'Event',     icon: '🎉' },
  contract_execution_object: { label: 'Trigger',   icon: '⚡' },
  surface_zone:              { label: 'Surface',   icon: '🖼' },
};

export function spatialTypeMeta(type: string | null | undefined): { label: string; icon: string } {
  if (type && (type as SpatialObjectType) in SPATIAL_TYPE_META) {
    return SPATIAL_TYPE_META[type as SpatialObjectType];
  }
  return { label: 'Object', icon: '•' };
}

const TO_RAD = Math.PI / 180;
const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance between two coords, in metres. */
export function haversineMeters(a: LngLatLite, b: LngLatLite): number {
  const φ1 = a.lat * TO_RAD;
  const φ2 = b.lat * TO_RAD;
  const Δφ = (b.lat - a.lat) * TO_RAD;
  const Δλ = (b.lng - a.lng) * TO_RAD;
  const h = Math.sin(Δφ / 2) ** 2
          + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Best-effort lng/lat extraction from a spatial-object row. Prefers
 * the precomputed `centroid` Point, falling back to the first finite
 * coordinate found anywhere in the raw geometry (handles a row whose
 * centroid hasn't been computed yet, e.g. right after a create).
 */
export function rowCenter(row: {
  centroid?: { coordinates?: [number, number] } | null;
  geometry?: { coordinates?: unknown } | null;
}): LngLatLite | null {
  const c = row.centroid;
  if (c?.coordinates && Number.isFinite(c.coordinates[0]) && Number.isFinite(c.coordinates[1])) {
    return { lng: c.coordinates[0], lat: c.coordinates[1] };
  }
  const g = row.geometry;
  if (!g) return null;
  const walk = (coords: unknown): [number, number] | null => {
    if (!Array.isArray(coords)) return null;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return [coords[0] as number, coords[1] as number];
    }
    for (const inner of coords) {
      const r = walk(inner);
      if (r) return r;
    }
    return null;
  };
  const first = walk((g as { coordinates?: unknown }).coordinates);
  return first ? { lng: first[0], lat: first[1] } : null;
}

/**
 * Whether `pt` falls inside a GeoJSON Polygon or MultiPolygon
 * geometry. Tolerant of malformed input (returns false).
 *
 * Backed by Turf's `booleanPointInPolygon`, which — unlike the prior
 * hand-rolled outer-ring-only ray-cast — correctly honours interior
 * rings (holes): a point inside an enclave/lake cut out of a country
 * polygon is now reported as OUTSIDE, matching the on-chain geofence
 * evaluation GeoLink performs. Turf is already a transitive dep of
 * mapbox-gl-draw, so this adds no meaningful bundle weight.
 */
export function pointInPolygon(
  pt: LngLatLite,
  geometry: { type?: string; coordinates?: unknown } | null | undefined,
): boolean {
  if (!geometry || !geometry.coordinates) return false;
  const type = geometry.type;
  if (type !== 'Polygon' && type !== 'MultiPolygon') return false;
  try {
    return booleanPointInPolygon(
      [pt.lng, pt.lat],
      geometry as unknown as Polygon | MultiPolygon,
    );
  } catch {
    // Malformed coordinates (unclosed rings, NaNs) — fail closed.
    return false;
  }
}

/** Format a metre distance for a compact pill ("120 m" / "3.4 km"). */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return '';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}
